import { useEffect, useState } from 'react'
import type { ExtendedQuote, LiveQuote } from '@shared/types'
import { api } from '../lib/api'
import { pollWhileVisible } from '../lib/utils'
import { normalizeSymbol } from '../lib/symbol'

export type MarketSession = 'pre' | 'regular' | 'after' | 'closed'

export const SESSION_LABEL: Record<MarketSession, string> = {
  pre: 'Pre-market',
  regular: 'Open',
  after: 'After-hours',
  closed: 'Closed',
}

/** US market session from current ET wall-clock (robust via Intl parts). */
export function usMarketSession(d: Date = new Date()): MarketSession {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  const wd = get('weekday')
  if (wd === 'Sat' || wd === 'Sun') return 'closed'
  let hour = parseInt(get('hour'), 10)
  if (Number.isNaN(hour)) return 'closed'
  if (hour === 24) hour = 0
  const mins = hour * 60 + (parseInt(get('minute'), 10) || 0)
  if (mins >= 240 && mins < 570) return 'pre' // 04:00–09:30
  if (mins >= 570 && mins < 960) return 'regular' // 09:30–16:00
  if (mins >= 960 && mins < 1200) return 'after' // 16:00–20:00
  return 'closed'
}

export function useMarketSession(): MarketSession {
  const [s, setS] = useState(() => usMarketSession())
  useEffect(() => {
    const t = setInterval(() => setS(usMarketSession()), 60_000)
    return () => clearInterval(t)
  }, [])
  return s
}

/**
 * Extended-hours (pre/after) quotes for US symbols, fetched whenever the market
 * is NOT in the regular session — including pre, after, AND closed (so the last
 * after-hours price keeps showing overnight). Keyed by full symbol (AAPL.US);
 * never includes non-US symbols (EODHD has no extended feed for them).
 */
export function useExtendedQuotes(
  symbols: string[],
  session: MarketSession,
): Record<string, ExtendedQuote> {
  const active = session !== 'regular'
  const usSyms = symbols.map(normalizeSymbol).filter((s) => s.endsWith('.US'))
  const key = active ? [...new Set(usSyms)].sort().join(',') : ''
  const [map, setMap] = useState<Record<string, ExtendedQuote>>({})

  useEffect(() => {
    if (!key) {
      setMap({})
      return
    }
    let alive = true
    const load = () =>
      api
        .extendedQuotes(key.split(','))
        .then((qs) => {
          if (!alive) return
          const m: Record<string, ExtendedQuote> = {}
          for (const q of qs) m[q.symbol] = q
          setMap(m)
        })
        .catch(() => {})
    // Pre/after: the WebSocket carries the live price; this is the reference — slow refresh, visible tabs only.
    const stop = pollWhileVisible(load, session === 'closed' ? 30 * 60_000 : 120_000)
    return () => {
      alive = false
      stop()
    }
  }, [key, session])

  return active ? map : {}
}

/**
 * Real-time prices via the server's EODHD WebSocket bridge (US-only). Polls the
 * server cache every 3s; the cache itself updates continuously from the live
 * stream, so this is real-time within network latency (regular + extended hours).
 */
export function useLiveQuotes(symbols: string[]): Record<string, LiveQuote> {
  const usSyms = symbols.map(normalizeSymbol).filter((s) => s.endsWith('.US'))
  const key = [...new Set(usSyms)].sort().join(',')
  const [map, setMap] = useState<Record<string, LiveQuote>>({})

  useEffect(() => {
    if (!key) {
      setMap({})
      return
    }
    let alive = true
    const load = () =>
      api
        .liveQuotes(key.split(','))
        .then((qs) => {
          if (!alive) return
          const m: Record<string, LiveQuote> = {}
          for (const q of qs) m[q.symbol] = q
          setMap(m)
        })
        .catch(() => {})
    const stop = pollWhileVisible(load, 3000)
    return () => {
      alive = false
      stop()
    }
  }, [key])

  return map
}

